import { useEffect, useMemo, useRef, useState } from 'react';
import { listFormVersionsDocsAggregate } from '../../workers/listFormVersionsDocsAggregate';
import { homePageConfig } from '#types';

interface UseHomePageReportCountsProps {
  formsTableQueryParams: {
    startDate: { _seconds: number; _nanoseconds: number };
    endDate: { _seconds: number; _nanoseconds: number };
  };
  userId: string | undefined;
  onError?: (error: unknown) => void;
}

export const useHomePageReportCounts = ({
  formsTableQueryParams,
  userId,
  onError,
}: UseHomePageReportCountsProps) => {
  const [reportCountsFromMetrics, setReportCountsFromMetrics] = useState<Record<string, number>>({});
  const reportMetricRequestIdRef = useRef(0);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError; // eslint-disable-line react-hooks/refs

  useEffect(() => {
    if (!userId) return;

    const countReports = homePageConfig.formsTable.reports.flatMap((report) => {
      if (report.metricConfig.type !== 'count') {
        return [];
      }
      return [{ id: report.id, where: report.metricConfig.where }];
    });

    if (countReports.length === 0) return;

    const requestId = reportMetricRequestIdRef.current + 1;
    reportMetricRequestIdRef.current = requestId;

    void (async () => {
      const results = await Promise.all(
        countReports.map(async (report) => {
          const result = await listFormVersionsDocsAggregate({
            startDate: formsTableQueryParams.startDate,
            endDate: formsTableQueryParams.endDate,
            where: report.where,
            aggregate: { type: 'count' },
          });
          return { reportId: report.id, count: result?.value ?? 0 };
        })
      );

      if (reportMetricRequestIdRef.current !== requestId) return;

      const counts: Record<string, number> = {};
      for (const r of results) {
        counts[r.reportId] = r.count;
      }
      setReportCountsFromMetrics(counts);
    })().catch((e) => {
      if (reportMetricRequestIdRef.current !== requestId) return;
      console.error('Failed to load report metrics', e);
      onErrorRef.current?.(e);
    });
  }, [formsTableQueryParams, userId]);

  const reportCounts = useMemo(() => {
    const counts: Record<string, number | undefined> = {};

    for (const report of homePageConfig.formsTable.reports) {
      counts[report.id] = reportCountsFromMetrics[report.id];
    }

    return counts;
  }, [reportCountsFromMetrics]);

  return reportCounts;
};
